/**
 * Assessment-actor picker + inline create (DC-1805, design §5.2).
 *
 * The attestation forms attribute evidence to a REGISTERED actor — the
 * bank's named party with a role title and a line of defense — or honestly
 * to nobody: "unattributed" is a legal choice, rendered downstream as
 * "line unstated", never guessed and never defaulted to a line.
 *
 * Inline create follows the registry's own weld: second/third/external
 * lines require an independence basis; first and governing refuse one —
 * the same rule the route schema and the table CHECK enforce, stated here
 * so the refusal never surprises.
 *
 * The registry is append-only (no edit anywhere): a wrong name or line is
 * corrected by a new row, and prior attributions keep pointing at the row
 * that was true when they were made.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { LINE_LABEL, type LineOfDefense } from './assessmentTypes';

export interface RegistryActor {
  id: string;
  displayName: string;
  roleTitle: string;
  lineOfDefense: LineOfDefense;
  independenceBasis: string | null;
}

const INDEPENDENT_LINES: readonly LineOfDefense[] = ['second', 'third', 'external'];

function requiresBasis(line: LineOfDefense): boolean {
  return INDEPENDENT_LINES.includes(line);
}

export const ActorPicker: React.FC<{
  /** Stable per-form id prefix for label wiring. */
  idPrefix: string;
  /** Copy for what the attribution means on this form. */
  label: string;
  value: string | null;
  onChange: (actorId: string | null) => void;
  disabled?: boolean;
}> = ({ idPrefix, label, value, onChange, disabled }) => {
  const { get, post } = useApiClient();
  const [actors, setActors] = useState<RegistryActor[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [line, setLine] = useState<LineOfDefense>('first');
  const [basis, setBasis] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await get<{ data?: { actors?: RegistryActor[] } }>('/api/assessment-actors');
      setActors(res.data?.actors ?? []);
    } catch {
      // The picker stays usable without the registry: attribution is
      // optional, and a failed read is stated, not silently emptied.
      setActors(null);
      setMessage('The actor registry could not be read — evidence can still be recorded unattributed.');
    }
  }, [get]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await post<{ data?: { actor?: RegistryActor } }>('/api/assessment-actors', {
        displayName: name.trim(),
        roleTitle: title.trim(),
        lineOfDefense: line,
        ...(requiresBasis(line) ? { independenceBasis: basis.trim() } : {}),
      });
      const actor = res.data?.actor;
      if (actor) {
        setActors((prev) => [actor, ...(prev ?? [])]);
        onChange(actor.id);
      }
      setCreating(false);
      setName('');
      setTitle('');
      setBasis('');
    } catch (error) {
      const detail = error instanceof Error ? error.message : '';
      setMessage(detail && !/^HTTP \d+$/.test(detail) ? detail : 'The actor could not be recorded.');
    } finally {
      setBusy(false);
    }
  }, [post, name, title, line, basis, onChange]);

  const canCreate =
    name.trim() !== '' &&
    title.trim() !== '' &&
    (!requiresBasis(line) || basis.trim() !== '');

  return (
    <div className="uc-actor-picker" data-testid={`${idPrefix}-actor-picker`}>
      <label className="uc-evidence-form-label" htmlFor={`${idPrefix}-actor-select`}>
        {label}
        <select
          id={`${idPrefix}-actor-select`}
          value={value ?? ''}
          disabled={disabled || busy}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        >
          <option value="">Unattributed — line unstated</option>
          {(actors ?? []).map((actor) => (
            <option key={actor.id} value={actor.id}>
              {actor.displayName} — {actor.roleTitle} · {LINE_LABEL[actor.lineOfDefense]}
            </option>
          ))}
        </select>
      </label>
      {!creating && (
        <button
          type="button"
          className="banking-btn banking-btn-secondary"
          disabled={disabled || busy}
          data-testid={`${idPrefix}-actor-new`}
          onClick={() => setCreating(true)}
        >
          New actor…
        </button>
      )}
      {creating && (
        <div className="uc-actor-create" data-testid={`${idPrefix}-actor-create`}>
          <label className="uc-evidence-form-label">
            Name
            <input value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="uc-evidence-form-label">
            Role title
            <input
              value={title}
              disabled={busy}
              placeholder="e.g. Head of Model Validation"
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="uc-evidence-form-label">
            Line of defense
            <select
              value={line}
              disabled={busy}
              onChange={(e) => setLine(e.target.value as LineOfDefense)}
            >
              <option value="first">First line — owns the risk</option>
              <option value="second">Second line — independent challenge</option>
              <option value="third">Third line — assurance</option>
              <option value="governing">Governing — board / senior management</option>
              <option value="external">External — beyond-the-lines assurance</option>
            </select>
          </label>
          {requiresBasis(line) ? (
            <label className="uc-evidence-form-label uc-evidence-form-wide">
              Independence basis
              <textarea
                value={basis}
                disabled={busy}
                rows={2}
                placeholder="What makes this role independent — rigor of review, authority to compel change, external arrangement."
                onChange={(e) => setBasis(e.target.value)}
              />
            </label>
          ) : (
            <p className="uc-evidence-form-hint">
              {line === 'first' ? 'The first line' : 'The governing layer'} does not claim
              independence, so no basis is recorded.
            </p>
          )}
          <button
            type="button"
            className="banking-btn banking-btn-primary"
            disabled={busy || !canCreate}
            data-testid={`${idPrefix}-actor-save`}
            onClick={() => void create()}
          >
            Add to registry
          </button>
          <button
            type="button"
            className="banking-btn banking-btn-secondary"
            disabled={busy}
            onClick={() => setCreating(false)}
          >
            Cancel
          </button>
          <p className="uc-evidence-form-hint">
            The registry is append-only: a wrong name, title, or line is corrected by a new
            entry, and existing evidence keeps pointing at the row it was attributed to.
          </p>
        </div>
      )}
      {message && (
        <p className="uc-evidence-refusal" role="alert">
          {message}
        </p>
      )}
    </div>
  );
};

export default ActorPicker;
